/*
  tr-size-finder.js — « Trouver ma taille », pour les vestes et les joggings.

  ─── D'où viennent les règles ───

  Rien n'est inventé ici. Deux points d'ancrage et une base, tous fournis
  par la marque :

  1. Les bandes de hauteur du client : XXS jusqu'à 164 cm, puis XS, S, M, L
     et XL par paliers. Elles se chevauchaient d'un centimètre d'une bande
     à l'autre — 164 appartenait à XXS et à XS — ce qui rendait deux
     réponses possibles pour la même saisie. Les bornes sont ici
     exclusives, et c'est la seule liberté prise sur la base reçue.

  2. Le mannequin homme du site : 185 cm, taille M. La bande 182–188 le
     place en M : les deux concordent, la base est cohérente.

  3. Le mannequin femme du site : 176 cm, taille M. La bande la placerait
     en S. On en avait d'abord tiré un décalage par genre ; le client a
     corrigé : elle porte du M **parce qu'elle le porte oversize**. Le
     rendu voulu est déjà une question posée à part, et le genre est donc
     sorti du calcul — ainsi que du formulaire, une question qui ne
     change rien n'ayant pas à être posée.

  La corpulence intervient par l'IMC, en appoint et jamais en principal :
  à hauteur égale, une carrure plus large demande un cran de plus. Les
  seuils sont ceux de l'OMS — surpoids à 25, obésité à 30, maigreur à
  18,5 — décalés vers le haut parce que la coupe est déjà ample : avec les
  seuils bruts, un gabarit ordinaire serait monté d'une taille pour rien.

  Toute règle qui contredirait les deux mannequins serait fausse par
  construction. C'est ce que vérifient les deux premiers tests.
*/

(function (racine) {
  'use strict';

  var ECHELLE = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

  // Les bandes du client, en centimètres, avec leurs deux bornes.
  //
  // La hauteur ne donne pas un cran mais une position continue entre les
  // crans : au milieu d'une bande elle vaut exactement son rang, à ses
  // bords presque un demi-cran de plus ou de moins. Arrondie seule, elle
  // retombe donc sur la bande du client, exactement.
  //
  // Pourquoi continue : la corpulence, elle, varie sans marche, et l'IMC
  // baisse quand on grandit à poids égal. Avec une hauteur en escalier,
  // les deux se contredisaient à l'intérieur d'une bande — 151 cm et
  // 60 kg donnaient du XS, 152 cm et 60 kg du XXS. Grandir faisait
  // rapetisser. Une hauteur continue monte plus vite que l'IMC ne
  // descend, et l'ordre est rétabli partout.
  //
  // Les deux bandes ouvertes reçoivent la largeur de leur voisine : sans
  // bornes, la pente n'existerait pas.
  var BANDES = [
    { bas: 156, haut: 164, rang: 0 },
    { bas: 165, haut: 173, rang: 1 },
    { bas: 174, haut: 181, rang: 2 },
    { bas: 182, haut: 188, rang: 3 },
    { bas: 189, haut: 195, rang: 4 },
    { bas: 196, haut: 204, rang: 5 },
  ];

  // Quatre dixièmes de cran de part et d'autre du centre, et non un demi.
  // Au demi exact, les bornes d'une bande frôlent la bascule d'arrondi :
  // le moindre appoint de corpulence les faisait sortir de leur propre
  // bande — 196 cm retombait en L au lieu de XL. Cette marge laisse la
  // bande tenir seule, sans empêcher une corpulence franche de décider.
  var AMPLITUDE_BANDE = 0.8;

  // Hors des bandes extrêmes, la pente se prolonge au lieu de s'aplatir.
  // Un palier plat laissait l'IMC commander seul : entre 140 et 156 cm à
  // poids constant, grandir faisait rapetisser. Le résultat sort de
  // l'échelle et s'y fait ramener plus loin — ce qui importe est que
  // l'ordre tienne sur toute la plage, pas seulement dans les bandes.
  function positionHauteur(cm) {
    var premiere = BANDES[0];
    var derniere = BANDES[BANDES.length - 1];
    if (cm < premiere.bas) {
      var penteBas = AMPLITUDE_BANDE / (premiere.haut - premiere.bas);
      return premiere.rang - AMPLITUDE_BANDE / 2 - (premiere.bas - cm) * penteBas;
    }
    if (cm > derniere.haut) {
      var penteHaut = AMPLITUDE_BANDE / (derniere.haut - derniere.bas);
      return derniere.rang + AMPLITUDE_BANDE / 2 + (cm - derniere.haut) * penteHaut;
    }
    for (var b = 0; b < BANDES.length; b += 1) {
      var bande = BANDES[b];
      if (cm <= bande.haut) {
        var part = (cm - bande.bas) / (bande.haut - bande.bas);
        return bande.rang + AMPLITUDE_BANDE * (Math.max(0, Math.min(1, part)) - 0.5);
      }
    }
    return derniere.rang;
  }

  // Garde-fous de saisie. Hors de ces bornes, on ne répond pas plutôt que
  // de répondre n'importe quoi : une recommandation fausse coûte un retour.
  // Calés sur les deux mannequins du site.
  //
  // Le mannequin homme, 185 cm, porte du M : la bande 182-188 le donne
  // déjà, les deux concordent.
  //
  // Le mannequin femme, 176 cm, porte du M là où la bande donne du S. On
  // en avait tiré un décalage par genre — à tort. Le client a corrigé :
  // elle porte du M **parce qu'elle le porte oversize**, pas parce
  // qu'elle est une femme. Le rendu voulu est déjà une question posée à
  // part dans l'outil ; le genre n'a donc plus de raison d'y entrer, et
  // il en est sorti plutôt que laissé à zéro — une question qui ne change
  // rien n'a pas à être posée.
  //
  // Vérification : 176 cm, 62 kg, rendu oversize donne bien M.
  var IMC_REFERENCE = 22;
  var PENTE_CORPULENCE = 0.16;

  var TAILLE_MIN = 120;
  var TAILLE_MAX = 230;
  var POIDS_MIN = 30;
  var POIDS_MAX = 250;

  function entier(valeur) {
    var n = Number(valeur);
    return Number.isFinite(n) ? n : NaN;
  }

  function recommanderTaille(entree) {
    var entreeSure = entree || {};
    var taille = entier(entreeSure.taille);
    var poids = entier(entreeSure.poids);
    var disponibles = entreeSure.tailles && entreeSure.tailles.length
      ? entreeSure.tailles
      : ECHELLE;

    if (
      !Number.isFinite(taille) || !Number.isFinite(poids) ||
      taille < TAILLE_MIN || taille > TAILLE_MAX ||
      poids < POIDS_MIN || poids > POIDS_MAX
    ) {
      return { taille: null, raisons: [] };
    }

    var raisons = [];

    // 1. La hauteur donne la base, en position continue.
    var rang = positionHauteur(taille);
    raisons.push({ cle: 'hauteur', valeur: ECHELLE[Math.max(0, Math.min(ECHELLE.length - 1, Math.round(rang)))] });

    // 2. La corpulence, en appoint — et de façon continue.
    //
    //    Les premiers seuils étaient des marches : à 1,70 m, 89 kg
    //    donnaient du M et 90 kg du L. Un kilo ne change pas de taille,
    //    et une recommandation qui saute sous le doigt ne se croit pas.
    //    L'écart à l'IMC de référence se convertit donc en fraction de
    //    taille, et l'arrondi n'a lieu qu'une fois, tout à la fin.
    //
    //    L'IMC de référence est celui que la bande de hauteur suppose
    //    déjà : une carrure moyenne. Les bornes empêchent une saisie
    //    extrême d'emporter la recommandation à elle seule.
    var metres = taille / 100;
    var imc = poids / (metres * metres);
    var appoint = (imc - IMC_REFERENCE) * PENTE_CORPULENCE;
    appoint = Math.max(-1.2, Math.min(2.2, appoint));
    rang += appoint;
    raisons.push({ cle: 'corpulence', valeur: Math.round(appoint * 100) / 100 });

    // 3. L'arrondi, une seule fois, puis le retour dans l'échelle. Borner
    //    ici et non après le choix de coupe : sinon un calcul sorti de
    //    l'échelle absorbe le cran demandé, et « oversize » ne déplace
    //    plus rien pour les gabarits extrêmes.
    rang = Math.round(rang);
    rang = Math.max(0, Math.min(ECHELLE.length - 1, rang));

    // 4. Le rendu voulu, qui a le dernier mot et déplace toujours d'une
    //    taille pleine : c'est le seul critère choisi en connaissance de
    //    cause, il ne doit pas se diluer dans un arrondi.
    if (entreeSure.coupe === 'oversize') {
      rang += 1;
      raisons.push({ cle: 'oversize', valeur: 1 });
    } else if (entreeSure.coupe === 'ajuste') {
      rang -= 1;
      raisons.push({ cle: 'ajuste', valeur: -1 });
    }

    // 5. Ramener dans l'échelle, puis dans ce que la fiche vend vraiment.
    //    Recommander une taille absente du produit serait une impasse.
    rang = Math.max(0, Math.min(ECHELLE.length - 1, rang));
    var voulue = ECHELLE[rang];

    if (disponibles.indexOf(voulue) === -1) {
      var rangsVendus = disponibles
        .map(function (t) { return ECHELLE.indexOf(t); })
        .filter(function (r) { return r !== -1; })
        .sort(function (a, b) { return a - b; });
      if (!rangsVendus.length) return { taille: null, raisons: raisons };
      var proche = rangsVendus[0];
      for (var j = 0; j < rangsVendus.length; j += 1) {
        if (Math.abs(rangsVendus[j] - rang) < Math.abs(proche - rang)) proche = rangsVendus[j];
      }
      voulue = ECHELLE[proche];
      raisons.push({ cle: 'ramenee', valeur: voulue });
    }

    return { taille: voulue, raisons: raisons, imc: Math.round(imc * 10) / 10 };
  }

  racine.TrSizeFinder = { recommanderTaille: recommanderTaille, ECHELLE: ECHELLE };


  /*
    L'interface. Deux curseurs plutôt que deux champs à remplir : on
    cherche sa taille en bougeant, pas en tapant.

    Pas de silhouette : elle avait été ajoutée pour accompagner le geste,
    elle alourdissait la fenêtre sans rien apprendre. Deux curseurs, trois
    boutons et une réponse suffisent, et c'est la mesure du site.

    Le résultat se recalcule à chaque mouvement, sans bouton à valider :
    la réponse suit la main, et l'on voit tout de suite ce que change un
    centimètre ou un cran de coupe.

    Le calcul est celui du haut de ce fichier, celui que les tests
    vérifient. L'interface ne décide de rien — et elle vit désormais dans
    la même enveloppe, ce qui lui rend la fonction visible. Elle était
    dehors, appelait un nom qu'elle ne voyait pas, et ne rendait donc
    jamais de réponse.
  */
  if (typeof customElements !== 'undefined' && !customElements.get('tr-size-finder')) {
    customElements.define(
      'tr-size-finder',
      class TrSizeFinder extends HTMLElement {
        connectedCallback() {
          this.boite = this.querySelector('[data-finder-dialog]');
          this.form = this.querySelector('[data-finder-form]');
          this.sortie = this.querySelector('[data-finder-result]');
          this.ajout = this.querySelector('[data-finder-add]');
          if (!this.boite || !this.form) return;

          try {
            this.libelles = JSON.parse(this.dataset.finderLabels);
          } catch (erreur) {
            this.libelles = {};
          }

          this.valeurs = {
            taille: this.querySelector('[data-finder-value="taille"]'),
            poids: this.querySelector('[data-finder-value="poids"]'),
          };

          this.querySelectorAll('[data-finder-open]').forEach((bouton) => {
            bouton.addEventListener('click', () => {
              if (typeof this.boite.showModal === 'function') this.boite.showModal();
              this.calculer();
            });
          });
          this.querySelectorAll('[data-finder-close]').forEach((bouton) => {
            bouton.addEventListener('click', () => this.boite.close());
          });
          this.boite.addEventListener('click', (evenement) => {
            if (evenement.target === this.boite) this.boite.close();
          });

          // `input` et non `change` : la réponse doit suivre le doigt,
          // pas attendre qu'on le lève.
          this.form.addEventListener('input', () => this.calculer());
          this.form.addEventListener('submit', (evenement) => evenement.preventDefault());

          if (this.ajout) {
            this.ajout.addEventListener('click', () => this.ajouterAuPanier());
          }

          this.calculer();
        }

        // Les tailles proposées sont lues sur la fiche elle-même, et non
        // recopiées dans un réglage : une taille retirée du produit
        // disparaît alors d'elle-même de la recommandation.
        taillesDuProduit() {
          const fiche = this.closest('tr-product-info') || document;
          const vues = [];
          fiche.querySelectorAll('.tr-pdp__size').forEach((noeud) => {
            const valeur = noeud.dataset.optionValue;
            if (valeur && vues.indexOf(valeur) === -1) vues.push(valeur);
          });
          return vues;
        }

        calculer() {
          const donnees = new FormData(this.form);
          const cm = Number(donnees.get('taille'));
          const kg = Number(donnees.get('poids'));

          if (this.valeurs.taille) this.valeurs.taille.textContent = cm;
          if (this.valeurs.poids) this.valeurs.poids.textContent = kg;

          const resultat = recommanderTaille({
            taille: cm,
            poids: kg,
            coupe: donnees.get('coupe'),
            tailles: this.taillesDuProduit(),
          });

          if (!this.sortie) return;

          // Démasquer sans condition : le balisage ne porte plus
          // d'attribut `hidden`, mais s'y fier était précisément l'erreur
          // qui a rendu l'outil muet. Le composant répond de ce qu'il
          // affiche, pas le gabarit.
          this.sortie.hidden = false;

          if (!resultat.taille) {
            this.sortie.textContent = this.libelles.invalid || '';
            this.sortie.classList.add('is-invalid');
            this.proposerAchat(null);
            return;
          }

          this.sortie.classList.remove('is-invalid');
          this.sortie.textContent = String(this.libelles.result || '%taille%').replace(
            '%taille%',
            resultat.taille
          );
          this.proposerAchat(resultat.taille);
        }

        // L'étiquette de la fiche qui porte cette taille. C'est elle qui
        // sait si la taille est achetable : la fiche y pose
        // `is-unavailable` quand la variante manque pour le coloris
        // courant. Le composant ne tient donc aucun état en double.
        etiquetteTaille(taille) {
          const fiche = this.closest('tr-product-info');
          if (!fiche || !taille) return null;
          const etiquettes = fiche.querySelectorAll('.tr-pdp__size');
          for (let i = 0; i < etiquettes.length; i += 1) {
            if (etiquettes[i].dataset.optionValue === taille) return etiquettes[i];
          }
          return null;
        }

        // La recommandation nomme la taille même en rupture — savoir
        // qu'on fait du M a de la valeur, et le réassort viendra. Le
        // bouton, lui, se tait : proposer d'ajouter au panier ce qui ne
        // peut pas l'être est une promesse qu'on ne tient pas.
        proposerAchat(taille) {
          this.tailleProposee = null;
          if (!this.ajout) return;
          const etiquette = this.etiquetteTaille(taille);
          if (!etiquette || etiquette.classList.contains('is-unavailable')) {
            this.ajout.hidden = true;
            return;
          }
          this.tailleProposee = taille;
          this.ajout.textContent = String(this.libelles.add || '%taille%').replace(
            '%taille%',
            taille
          );
          this.ajout.hidden = false;
        }

        // On ne refait pas l'ajout au panier : on coche la taille, on
        // prévient la fiche, et on actionne son propre bouton. Tout ce
        // qui pend à cet ajout — panier latéral, stock, erreurs — reste
        // donc vrai sans être dupliqué ici.
        ajouterAuPanier() {
          const etiquette = this.etiquetteTaille(this.tailleProposee);
          if (!etiquette) return;
          const input = etiquette.querySelector('.tr-pdp__input');
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (this.boite && typeof this.boite.close === 'function') this.boite.close();
          const fiche = this.closest('tr-product-info');
          const atc = fiche && fiche.querySelector('.tr-pdp__atc');
          if (atc) atc.click();
        }
      }
    );
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
